import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Typography,
  Tooltip,
  Divider,
  Col,
  Row,
  Upload,
  UploadFile,
  Modal,
  DatePicker,
} from "antd";
import { PlusCircleFilled, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import CreateEntityInfo from "../../../shared/CreateEntityInfo";
import { ReactComponent as UploadIcon } from "../../../../assets/images/upload.svg";
import showNotification from "../../../../services/notificationService";
import { ReactComponent as ModuleIcon } from "../../../../assets/images/reusable-items/module.svg";
import { ReactComponent as CourseIcon } from "../../../../assets/images/reusable-items/course.svg";
import { ReactComponent as LessonIcon } from "../../../../assets/images/reusable-items/lesson.svg";
import { ReactComponent as QuizzieIcon } from "../../../../assets/images/reusable-items/quizzie.svg";
import MenuItems from "../../../../assets/images/menu-items";
import DepartmentSelector from "../../../shared/DepartmentSelector";
import PositionSelector from "../../../shared/PositionSelector";
import UserSelector from "../../../shared/UserSelector";

const { Text } = Typography;

const EditOnboardingScreen = (props: any) => {
  const {
    onboarding,
    modules,
    courses,
    lessons,
    quizzies,
    tasks,
    departments,
    positions,
    users,
    loading,
    actions: {
      getOnboardingById,
      editOnboarding,
      getModule,
      getCourse,
      getLesson,
      getQuizzie,
      getTask,
      getDepartment,
      getPosition,
      getUserByRole,
    },
  } = props;

  const [fileList, setFileList] = useState<any>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [stepItems, setStepItems] = useState<any>([]);
  const [subItems, setSubItems] = useState<any>(null);
  const [deletedSteps, setDeletedSteps] = useState<string[]>([]);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      getOnboardingById({ id });
    }
  }, [id]);

  useEffect(() => {
    getModule();
    getCourse();
    getLesson();
    getQuizzie();
    getTask();
    getDepartment();
    getPosition();
    getUserByRole("user");
  }, []);

  useEffect(() => {
    if (id && !!onboarding) {
      form.setFieldsValue({
        ...onboarding,
        deadlineDate: dayjs(onboarding.deadlineDate),
        coverImage: onboarding?.coverImageKey,
      });
    }

    if (onboarding?.coverImageKey) {
      setFileList([
        {
          url: `${process.env.REACT_APP_CLOUDFRONT_KEY}/${onboarding.coverImageKey}`,
        },
      ]);
    }
    if (onboarding?.assignments?.departments?.length > 0) {
      setSelectedDepartments(
        onboarding?.assignments?.departments.map(
          (department: any) => department.id
        )
      );
    }
    if (onboarding?.assignments?.positions.length > 0) {
      setSelectedPositions(
        onboarding?.assignments?.positions.map((position: any) => position.id)
      );
    }
    if (onboarding?.assignments?.users.length > 0) {
      setSelectedUsers(
        onboarding?.assignments?.users.map((user: any) => user.id)
      );
    }

    if (onboarding?.steps?.length > 0) {
      setStepItems(
        [...(onboarding?.steps || [])]
          ?.sort((a: any, b: any) => a?.orderIndex - b?.orderIndex)
          .map((step: any) => {
            const item =
              step.course ||
              step.module ||
              step.lesson ||
              step.quizzie ||
              step.task;

            let type = "";
            let icon = null;
            if (step.course) {
              type = "Course";
              icon = <CourseIcon className="size-5 fill-primary-500" />;
            }
            if (step.module) {
              type = "Module";
              icon = <ModuleIcon className="size-5 fill-primary-500" />;
            }
            if (step.lesson) {
              type = "Lesson";
              icon = <LessonIcon className="size-5 fill-primary-500" />;
            }
            if (step.quizzie) {
              type = "Quizzie";
              icon = <QuizzieIcon className="size-5 fill-primary-500" />;
            }
            if (step.task) {
              type = "Task";
              icon = (
                <MenuItems.Task className="text-xl w-5 text-primary-500 " />
              );
            }

            return {
              ...item,
              type,
              icon,
              orderIndex: step.orderIndex,
              stepId: step.id,
            };
          })
      );
    }
  }, [onboarding]);

  const onFinisOnboarding = (values: any) => {
    if (
      selectedDepartments.length === 0 &&
      selectedPositions.length === 0 &&
      selectedUsers.length === 0
    ) {
      showNotification(
        "error",
        "At least one department or one position or one user must be selected"
      );
      return;
    }
    if (stepItems.length === 0) {
      showNotification("error", "At least one onboarding step must be added");
      return;
    }

    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("description", values.description);
    formData.append("deadlineDate", values.deadlineDate.format("YYYY-MM-DD"));

    if (fileList.length > 0 && fileList[0]?.originFileObj) {
      formData.append("coverImage", fileList[0].originFileObj as File);
    }

    selectedDepartments.forEach((departmentId: string) => {
      formData.append("departmentIds", departmentId);
    });
    selectedPositions.forEach((positionId: string) => {
      formData.append("positionIds", positionId);
    });
    selectedUsers.forEach((userId: string) => {
      formData.append("userIds", userId);
    });

    if (deletedSteps.length > 0) {
      deletedSteps
        ?.filter((step) => step)
        .forEach((step: string) => {
          formData.append("deletedSteps", step);
        });
    }

    const newSteps = stepItems?.map((step: any, index: number) => {
      if (!step?.newItem) return;

      switch (step.type) {
        case "Module":
          return {
            moduleId: step.id,
            orderIndex: index + 1,
          };
        case "Course":
          return {
            courseId: step.id,
            orderIndex: index + 1,
          };
        case "Lesson":
          return {
            lessonId: step.id,
            orderIndex: index + 1,
          };
        case "Quizzie":
          return {
            quizzieId: step.id,
            orderIndex: index + 1,
          };
        case "Task":
          return {
            taskId: step.id,
            orderIndex: index + 1,
          };
      }
    });

    newSteps
      ?.filter((step: any) => step)
      .forEach((step: any, i: number) => {
        if (
          step &&
          step.moduleId &&
          typeof step.moduleId === "string" &&
          step.moduleId.includes("-")
        ) {
          formData.append(`newSteps[${i}][moduleId]`, step.moduleId);
          formData.append(`newSteps[${i}][orderIndex]`, step.orderIndex);
        }
        if (
          step &&
          step.courseId &&
          typeof step.courseId === "string" &&
          step.courseId.includes("-")
        ) {
          formData.append(`newSteps[${i}][courseId]`, step.courseId);
          formData.append(`newSteps[${i}][orderIndex]`, step.orderIndex);
        }
        if (
          step &&
          step.lessonId &&
          typeof step.lessonId === "string" &&
          step.lessonId.includes("-")
        ) {
          formData.append(`newSteps[${i}][lessonId]`, step.lessonId);
          formData.append(`newSteps[${i}][orderIndex]`, step.orderIndex);
        }
        if (
          step &&
          step.quizzieId &&
          typeof step.quizzieId === "string" &&
          step.quizzieId.includes("-")
        ) {
          formData.append(`newSteps[${i}][quizzieId]`, step.quizzieId);
          formData.append(`newSteps[${i}][orderIndex]`, step.orderIndex);
        }
        if (
          step &&
          step.taskId &&
          typeof step.taskId === "string" &&
          step.taskId.includes("-")
        ) {
          formData.append(`newSteps[${i}][taskId]`, step.taskId);
          formData.append(`newSteps[${i}][orderIndex]`, step.orderIndex);
        }
      });

    const updatedSteps = stepItems?.map((step: any, index: number) => {
      if (step?.orderIndex === index + 1 || step.newItem) return;

      return {
        stepId: step.stepId,
        orderIndex: index + 1,
      };
    });

    updatedSteps
      ?.filter((step: any) => step)
      .forEach((step: any, i: number) => {
        if (
          step.stepId &&
          typeof step.stepId === "string" &&
          step.stepId.includes("-")
        ) {
          formData.append(`updatedSteps[${i}][id]`, step.stepId);
          formData.append(`updatedSteps[${i}][orderIndex]`, step.orderIndex);
        }
      });

    editOnboarding({
      id: onboarding.id,
      onboarding: formData,
      navigate,
    });
  };

  const handleImageChange = ({ fileList }: { fileList: UploadFile[] }) => {
    setFileList(fileList);
    setIsFormChanged(true);
  };

  const handleDepartmentChange = (departments: string[]) => {
    setSelectedDepartments(departments);
    setIsFormChanged(true);
  };

  const handlePositionChange = (positions: string[]) => {
    setSelectedPositions(positions);
    setIsFormChanged(true);
  };

  const handleUserChange = (users: string[]) => {
    setSelectedUsers(users);
    setIsFormChanged(true);
  };

  const stepOptions = [
    {
      icon: <ModuleIcon className="size-5 fill-primary-500" />,
      title: "Module",
    },
    {
      icon: <CourseIcon className="size-5 fill-primary-500" />,
      title: "Course",
    },
    {
      icon: <LessonIcon className="size-5 fill-primary-500" />,
      title: "Lesson",
    },
    {
      icon: <QuizzieIcon className="size-5 fill-primary-500" />,
      title: "Quizzie",
    },
    {
      icon: <MenuItems.Task className="text-xl w-5 text-primary-500" />,
      title: "Task",
    },
  ];

  const onChangeSubItems = (item: string) => {
    switch (item) {
      case "Module":
        modules.length === 0
          ? showNotification("error", "There are currently no Modules")
          : setSubItems({ items: modules, type: "Module" });
        break;
      case "Course":
        courses.length === 0
          ? showNotification("error", "There are currently no Courses")
          : setSubItems({ items: courses, type: "Course" });
        break;
      case "Lesson":
        lessons.length === 0
          ? showNotification("error", "There are currently no Lessons")
          : setSubItems({ items: lessons, type: "Lesson" });
        break;
      case "Quizzie":
        quizzies.length === 0
          ? showNotification("error", "There are currently no Quizzies")
          : setSubItems({ items: quizzies, type: "Quizzie" });
        break;
      case "Task":
        tasks.length === 0
          ? showNotification("error", "There are currently no Tasks")
          : setSubItems({ items: tasks, type: "Task" });
        break;
    }
  };

  const handleFormChange = () => {
    setIsFormChanged(form.isFieldsTouched() || fileList.length > 0);
  };

  return (
    <>
      <Form
        form={form}
        name="onboardingForm"
        autoComplete="off"
        size="small"
        className="h-full"
        onFinish={onFinisOnboarding}
        onValuesChange={handleFormChange}
      >
        <Row>
          <Col lg={8}>
            <CreateEntityInfo
              title="Onboarding Steps"
              text="Lorem ipsum dolor sit, amet consectetur adipisicing elit. Fugit pariatur perferendis expedita libero, sequi tempora aliquam
"
            ></CreateEntityInfo>
          </Col>
          <Col lg={16}>
            <div style={{ width: "100%", maxWidth: 600 }}>
              <Text className="text-neutral-600 text-base font-semibold">
                Title
              </Text>
              <Form.Item
                name="title"
                rules={[
                  {
                    required: true,
                    message: `Please input title!`,
                  },
                ]}
                className="mt-2"
              >
                <Input type="string" placeholder="Type the title here" />
              </Form.Item>

              <Text className="text-neutral-600 text-base font-semibold">
                Description
              </Text>
              <Form.Item
                name="description"
                rules={[
                  {
                    required: true,
                    message: `Please input description!`,
                  },
                ]}
                className="mt-2"
              >
                <Input type="string" placeholder="Type the description here" />
              </Form.Item>

              <Text className="text-neutral-600 text-base font-semibold">
                Cover Image
              </Text>
              <Form.Item
                name="coverImage"
                rules={[
                  {
                    required: true,
                    message: `Please upload a cover image!`,
                  },
                ]}
              >
                <Upload
                  name="coverImage"
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={() => false}
                  accept="image/png, image/jpeg, image/gif"
                  className="bg-white rounded-md h-[310px] mt-2 sticky"
                  fileList={fileList}
                  onChange={handleImageChange}
                >
                  {fileList.length === 0 && (
                    <div className="flex flex-col items-center justify-center">
                      <UploadIcon />
                      <p className="text-neutral-600 text-base font-medium">
                        Upload a cover image
                      </p>
                      <p className="text-xs text-neutral-500 font-normal">
                        PNG, JPG, GIF up to 5MB
                      </p>
                    </div>
                  )}
                </Upload>
              </Form.Item>

              <div className="mt-10 mb-8 flex flex-col gap-2">
                <Text className="text-neutral-600 text-base font-semibold">
                  Assign Onboarding
                </Text>
                <DepartmentSelector
                  departments={onboarding?.assignments?.departments}
                  onSelectionChange={handleDepartmentChange}
                  selectedDepartments={selectedDepartments}
                  setSelectedDepartments={setSelectedDepartments}
                />
                <PositionSelector
                  positions={onboarding?.assignments?.positions}
                  onSelectionChange={handlePositionChange}
                  selectedPositions={selectedPositions}
                  setSelectedPositions={setSelectedPositions}
                />
                <UserSelector
                  users={onboarding?.assignments?.users}
                  onSelectionChange={handleUserChange}
                  selectedUsers={selectedUsers}
                  setSelectedUsers={setSelectedUsers}
                />
              </div>

              <Text className="text-neutral-600 text-base font-semibold">
                Deadline
              </Text>
              <Form.Item
                name="deadlineDate"
                rules={[
                  {
                    required: true,
                    message: "Please select deadline!",
                  },
                ]}
                className="mt-2 "
              >
                <DatePicker
                  placeholder="Select deadline date"
                  size="large"
                  className="w-full h-[50px] px-4"
                  disabledDate={(current) =>
                    !current || current.isBefore(dayjs())
                  }
                  allowClear={false}
                />
              </Form.Item>

              {stepItems.length > 0 && (
                <Text className="text-neutral-600 text-base font-semibold">
                  Onboarding Steps
                </Text>
              )}

              <div className="mt-2">
                <div className="max-h-[calc(100vh-450px)] overflow-auto">
                  {stepItems.map((field: any, index: number) => (
                    <div key={field.id}>
                      <div className="w-full h-[50px] bg-white rounded-lg flex items-center pl-4 mb-2 justify-between">
                        <div className="flex items-center gap-4">
                          <div className=" bg-primary-500 text-white size-6 rounded-full flex items-center justify-center">
                            {index + 1}
                          </div>
                          {field.icon}
                          <Text className="-ml-2">{field.title}</Text>
                        </div>

                        <Tooltip title="Delete step">
                          <Button
                            type="text"
                            className="text-danger-500 mr-3 "
                            shape="circle"
                            icon={
                              <DeleteOutlined style={{ fontSize: "18px" }} />
                            }
                            onClick={() => {
                              setStepItems((prev: any) =>
                                prev.filter((s: any) => s.title !== field.title)
                              );
                              setDeletedSteps((prev) => [
                                ...prev,
                                field.stepId,
                              ]);
                              setIsFormChanged(true);
                            }}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="w-full flex justify-center items-center mt-6">
                  <Button
                    onClick={() => setShowModal(true)}
                    icon={<PlusCircleFilled />}
                    type="link"
                  >
                    Add new step
                  </Button>
                </div>
                <Divider style={{ borderColor: "#BECBFE" }}></Divider>
                <Modal
                  title="Add New Step"
                  open={showModal}
                  onCancel={() => {
                    setShowModal(false);
                    setSubItems(null);
                  }}
                  footer={null}
                >
                  <div className="mt-3">
                    {stepOptions.map((content, index) => (
                      <div key={index}>
                        <Button
                          key={index}
                          onClick={() => {
                            onChangeSubItems(content.title);
                          }}
                          className="w-full h-[50px] bg-neutral-100 border-none rounded-lg flex items-center justify-start gap-3 px-4 mb-2"
                        >
                          {content.icon}
                          <Text className="text-neutral-700 font-semibold text-base leading-4">
                            {content.title}
                          </Text>
                        </Button>

                        {subItems &&
                          subItems.items.length > 0 &&
                          subItems.type === content.title && (
                            <div className=" w-full max-h-[calc(100vh-480px)] overflow-auto py-2 rounded-lg px-4 my-2">
                              <div className="flex flex-col gap-2 ">
                                {subItems.items.map((item: any) => (
                                  <Button
                                    onClick={() => {
                                      setStepItems((prev: any) => [
                                        ...prev,
                                        {
                                          ...item,
                                          icon: content.icon,
                                          type: content.title,
                                          newItem: true,
                                        },
                                      ]);

                                      setShowModal(false);
                                      setSubItems(null);
                                      setIsFormChanged(true);
                                    }}
                                    key={item.id}
                                    className=" py-5 rounded-lg px-4 bg-neutral-100 text-neutral-700 text-sm flex justify-start cursor-pointer"
                                    disabled={stepItems?.some(
                                      (step: any) => step.id === item.id
                                    )}
                                  >
                                    {item.title}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                </Modal>
              </div>

              <div className="flex justify-end">
                <Button
                  className="w-1/2 h-[50px] text-base bg-primary-600 font-semibold rounded-lg my-5"
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={!isFormChanged || fileList.length === 0 || loading}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default EditOnboardingScreen;
